/** Shared Theme Creator payload shapes (server + client). */

/**
 * Where decorations appear.
 * - Special: `sidebar`, `background`, `all`
 * - Screen keys: `home`, `settings`, `shop`, … (see THEME_SCREEN_ELEMENT_IDS)
 */
export type ThemeScreenTarget = string;

/** Maps theme screen keys → DOM element ids. */
export const THEME_SCREEN_ELEMENT_IDS: Record<string, string> = {
  home: "startScreen",
  settings: "settingsScreen",
  shop: "shopScreen",
  inventory: "inventoryScreen",
  challenges: "challengesScreen",
  achievements: "achievementsScreen",
  profile: "profileScreen",
  leaderboard: "leaderboardScreen",
  news: "newsScreen",
  notifications: "notificationsScreen",
  wall: "wallScreen",
  gamehub: "gameHubScreen",
  modes: "modeScreen",
  sandbox: "sandboxScreen",
  practice: "practiceScreen",
  campaign: "campaignScreen",
  credits: "creditsScreen",
  stats: "statsScreen",
  auth: "authScreen",
  worldmap: "worldMapScreen",
};

export type ThemeColors = {
  backgroundColor: string;
  backgroundGradient: boolean;
  backgroundGradientTo: string;
  buttonsColor: string;
  textColor: string;
  buttonsGradient: boolean;
  buttonsGradientTo: string;
  textGradient: boolean;
  textGradientTo: string;
  ballsColor: string;
  ballsGradient: boolean;
  ballsGradientTo: string;
  /** When true, keep the player's Settings colour for this channel. */
  dontChangeBackground: boolean;
  dontChangeBalls: boolean;
  dontChangeButtons: boolean;
  dontChangeText: boolean;
};

/** Procedural particle emitter settings (Theme Creator Particle System). */
export type ThemeParticleConfig = {
  /** New particles per second. */
  spawnRate: number;
  /** Fall / travel speed along wind axis (px/s). */
  fallSpeedMin: number;
  fallSpeedMax: number;
  /** Lateral sine sway amplitude in px. */
  driftAmplitude: number;
  /** Lateral sine frequency (cycles per second). */
  driftFrequency: number;
  /** Rotation speed deg/s. */
  rotationSpeedMin: number;
  rotationSpeedMax: number;
  /** Size multiplier range. */
  scaleMin: number;
  scaleMax: number;
  /** Fade in at spawn and out near exit edge. */
  opacityFade: boolean;
  /**
   * Travel direction in degrees: 0 = straight down, positive tilts toward +X (right),
   * 180 = upward (embers), ±45 = diagonal rain/leaves.
   */
  windAngleDeg: number;
  /** Cap simultaneous particles. */
  maxParticles: number;
  /** Base draw size in CSS pixels at scale 1. */
  baseSizePx: number;
};

export type ThemeParticlePresetId = "snow" | "rain" | "leaves" | "embers";

export const THEME_PARTICLE_PRESETS: Record<
  ThemeParticlePresetId,
  { label: string; config: ThemeParticleConfig }
> = {
  snow: {
    label: "Snow",
    config: {
      spawnRate: 28,
      fallSpeedMin: 28,
      fallSpeedMax: 70,
      driftAmplitude: 28,
      driftFrequency: 0.35,
      rotationSpeedMin: -20,
      rotationSpeedMax: 20,
      scaleMin: 0.45,
      scaleMax: 1.15,
      opacityFade: true,
      windAngleDeg: 8,
      maxParticles: 120,
      baseSizePx: 14,
    },
  },
  rain: {
    label: "Rain",
    config: {
      spawnRate: 55,
      fallSpeedMin: 420,
      fallSpeedMax: 620,
      driftAmplitude: 4,
      driftFrequency: 0.1,
      rotationSpeedMin: 0,
      rotationSpeedMax: 0,
      scaleMin: 0.7,
      scaleMax: 1.1,
      opacityFade: true,
      windAngleDeg: 18,
      maxParticles: 160,
      baseSizePx: 10,
    },
  },
  leaves: {
    label: "Leaves",
    config: {
      spawnRate: 12,
      fallSpeedMin: 40,
      fallSpeedMax: 95,
      driftAmplitude: 55,
      driftFrequency: 0.55,
      rotationSpeedMin: -140,
      rotationSpeedMax: 140,
      scaleMin: 0.55,
      scaleMax: 1.4,
      opacityFade: true,
      windAngleDeg: -12,
      maxParticles: 70,
      baseSizePx: 18,
    },
  },
  embers: {
    label: "Embers / Sparks",
    config: {
      spawnRate: 18,
      fallSpeedMin: 35,
      fallSpeedMax: 90,
      driftAmplitude: 22,
      driftFrequency: 0.7,
      rotationSpeedMin: -60,
      rotationSpeedMax: 60,
      scaleMin: 0.4,
      scaleMax: 1.2,
      opacityFade: true,
      windAngleDeg: 180,
      maxParticles: 90,
      baseSizePx: 12,
    },
  },
};

export const DEFAULT_PARTICLE_CONFIG: ThemeParticleConfig = {
  ...THEME_PARTICLE_PRESETS.snow.config,
};

export type ThemeDecoration = {
  id: string;
  kind: "static" | "animated" | "particles";
  imageData: string;
  frames?: string[];
  anchor: "viewport" | "sidebar";
  xPct: number;
  yPct: number;
  widthPct: number;
  heightPct: number;
  screens: ThemeScreenTarget[];
  zIndex?: number;
  /** 0–1 display opacity in-game (default 1). */
  opacity?: number;
  /** `back` renders behind UI (background animations / particles). */
  layer?: "front" | "back";
  /** Present when kind === "particles". */
  particleConfig?: ThemeParticleConfig;
  /**
   * Optional CSS selector for an element to stick to (hub cards, START, widgets).
   * When set, x/y/w/h % are relative to that element's box and follow scroll/transforms.
   */
  attachSelector?: string;
  /** Degrees clockwise (0–359). */
  rotationDeg?: number;
};

export type ThemeData = {
  colors: ThemeColors;
  decorations: ThemeDecoration[];
  /** Optional procedural chip track composed in Theme Creator → Music. */
  music?: ThemeMusicData;
};

/** Web Audio oscillator types used by the game's procedural chip music. */
export type ThemeMusicOscillator = "sine" | "square" | "sawtooth" | "triangle";

/** Where the theme track replaces the player's arcade song. */
export type ThemeMusicScope = "menu" | "game" | "both";

/** One lit cell on the tracker grid (note row + absolute step). */
export type ThemeMusicCell = {
  /** 0 = highest pitch row (top of grid). */
  note: number;
  /** Absolute step index across all bars (0 .. bars * 16 - 1). */
  step: number;
};

export type ThemeMusicData = {
  /** When false (or no cells), normal arcade music plays. */
  enabled: boolean;
  bpm: number;
  /** Number of 16-step bars chained in the loop. */
  bars: number;
  oscillator: ThemeMusicOscillator;
  scope: ThemeMusicScope;
  cells: ThemeMusicCell[];
};

export const THEME_MUSIC_STEPS_PER_BAR = 16;
/** Two octaves chromatic + top C → 25 rows (C3…C5). */
export const THEME_MUSIC_NOTE_ROWS = 25;
/** MIDI number for the lowest row (bottom of grid) = C3. */
export const THEME_MUSIC_LOW_MIDI = 48;
export const THEME_MUSIC_OSCILLATORS: ThemeMusicOscillator[] = [
  "square",
  "triangle",
  "sawtooth",
  "sine",
];

export const DEFAULT_THEME_MUSIC: ThemeMusicData = {
  enabled: false,
  bpm: 120,
  bars: 1,
  oscillator: "square",
  scope: "both",
  cells: [],
};

export function themeMusicNoteToMidi(noteIndex: number): number {
  const n = Math.max(0, Math.min(THEME_MUSIC_NOTE_ROWS - 1, Math.round(noteIndex)));
  return THEME_MUSIC_LOW_MIDI + (THEME_MUSIC_NOTE_ROWS - 1 - n);
}

export function themeMusicMidiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** Duration of one 16th-note step in ms at the given BPM (4/4 bar = 16 steps). */
export function themeMusicStepMs(bpm: number): number {
  const b = Math.max(40, Math.min(240, bpm));
  return 60000 / b / 4;
}

export function themeMusicTotalSteps(music: ThemeMusicData): number {
  return Math.max(1, music.bars) * THEME_MUSIC_STEPS_PER_BAR;
}

export function normalizeThemeMusic(raw: unknown): ThemeMusicData | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const oscRaw = typeof o.oscillator === "string" ? o.oscillator : "square";
  const oscillator: ThemeMusicOscillator = THEME_MUSIC_OSCILLATORS.includes(
    oscRaw as ThemeMusicOscillator,
  )
    ? (oscRaw as ThemeMusicOscillator)
    : "square";
  const scopeRaw = o.scope === "menu" || o.scope === "game" || o.scope === "both" ? o.scope : "both";
  const bars = Math.max(1, Math.min(8, Math.round(clampNum(o.bars, 1, 1, 8))));
  const total = bars * THEME_MUSIC_STEPS_PER_BAR;
  const cells: ThemeMusicCell[] = [];
  const seen = new Set<string>();
  if (Array.isArray(o.cells)) {
    for (const c of o.cells) {
      if (!c || typeof c !== "object") continue;
      const row = c as Record<string, unknown>;
      const noteN =
        typeof row.note === "number" && Number.isFinite(row.note) ? Math.round(row.note) : -1;
      const stepN =
        typeof row.step === "number" && Number.isFinite(row.step) ? Math.round(row.step) : -1;
      if (noteN < 0 || noteN >= THEME_MUSIC_NOTE_ROWS) continue;
      if (stepN < 0 || stepN >= total) continue;
      const key = `${noteN}:${stepN}`;
      if (seen.has(key)) continue;
      seen.add(key);
      cells.push({ note: noteN, step: stepN });
      if (cells.length >= 512) break;
    }
  }
  return {
    enabled: o.enabled === true,
    bpm: Math.round(clampNum(o.bpm, DEFAULT_THEME_MUSIC.bpm, 40, 240)),
    bars,
    oscillator,
    scope: scopeRaw,
    cells,
  };
}

/** True when this payload should override arcade music in-game. */
export function themeMusicIsActive(music: ThemeMusicData | null | undefined): boolean {
  return !!music && music.enabled === true && music.cells.length > 0;
}

export const DEFAULT_THEME_COLORS: ThemeColors = {
  backgroundColor: "#000000",
  backgroundGradient: false,
  backgroundGradientTo: "#1a1a2e",
  buttonsColor: "#66ffff",
  textColor: "#66ffff",
  buttonsGradient: true,
  buttonsGradientTo: "#317a7a",
  textGradient: false,
  textGradientTo: "#a3ffff",
  ballsColor: "#66ffff",
  ballsGradient: false,
  ballsGradientTo: "#a3ffff",
  dontChangeBackground: false,
  dontChangeBalls: false,
  dontChangeButtons: false,
  dontChangeText: false,
};

export function emptyThemeData(): ThemeData {
  return {
    colors: { ...DEFAULT_THEME_COLORS },
    decorations: [],
    music: { ...DEFAULT_THEME_MUSIC, cells: [] },
  };
}

function normalizeScreen(s: unknown): ThemeScreenTarget | null {
  if (typeof s !== "string") return null;
  const key = s.trim().toLowerCase();
  if (!key || key.length > 40) return null;
  if (!/^[a-z][a-z0-9_-]*$/.test(key)) return null;
  return key;
}

function clampNum(v: unknown, fallback: number, min: number, max: number): number {
  const n = typeof v === "number" && Number.isFinite(v) ? v : fallback;
  return Math.max(min, Math.min(max, n));
}

export function normalizeParticleConfig(raw: unknown): ThemeParticleConfig {
  const d = DEFAULT_PARTICLE_CONFIG;
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const fallMin = clampNum(o.fallSpeedMin, d.fallSpeedMin, 1, 2000);
  const fallMax = clampNum(o.fallSpeedMax, d.fallSpeedMax, fallMin, 2000);
  const scaleMin = clampNum(o.scaleMin, d.scaleMin, 0.05, 5);
  const scaleMax = clampNum(o.scaleMax, d.scaleMax, scaleMin, 5);
  const rotMin = clampNum(o.rotationSpeedMin, d.rotationSpeedMin, -720, 720);
  const rotMax = clampNum(o.rotationSpeedMax, d.rotationSpeedMax, rotMin, 720);
  return {
    spawnRate: clampNum(o.spawnRate, d.spawnRate, 0.1, 200),
    fallSpeedMin: fallMin,
    fallSpeedMax: fallMax,
    driftAmplitude: clampNum(o.driftAmplitude, d.driftAmplitude, 0, 400),
    driftFrequency: clampNum(o.driftFrequency, d.driftFrequency, 0, 5),
    rotationSpeedMin: rotMin,
    rotationSpeedMax: rotMax,
    scaleMin,
    scaleMax,
    opacityFade: o.opacityFade !== false,
    windAngleDeg: clampNum(o.windAngleDeg, d.windAngleDeg, -360, 360),
    maxParticles: Math.round(clampNum(o.maxParticles, d.maxParticles, 1, 400)),
    baseSizePx: clampNum(o.baseSizePx, d.baseSizePx, 4, 96),
  };
}

/** Detect which game screen is currently visible (for Add Here placement). */
export function detectCurrentThemeScreen(): ThemeScreenTarget {
  if (typeof document === "undefined") return "home";
  if (
    document.body.classList.contains("challenge-lobby") ||
    document.body.classList.contains("challenge-active")
  ) {
    return "challenges";
  }
  for (const [key, id] of Object.entries(THEME_SCREEN_ELEMENT_IDS)) {
    const el = document.getElementById(id);
    if (el && !el.classList.contains("hidden")) return key;
  }
  const screens = document.querySelectorAll(".screen");
  for (const el of screens) {
    if (!(el instanceof HTMLElement) || el.classList.contains("hidden")) continue;
    const id = el.id || "";
    if (id === "adminPanelScreen") continue;
    for (const [key, mapped] of Object.entries(THEME_SCREEN_ELEMENT_IDS)) {
      if (mapped === id) return key;
    }
    if (id.endsWith("Screen")) return id.replace(/Screen$/, "").toLowerCase();
  }
  return "home";
}

/** Whether a theme screen key is currently visible in the DOM. */
export function isThemeScreenVisible(key: ThemeScreenTarget, playing = false): boolean {
  if (key === "background" || key === "all") return !playing;
  if (key === "sidebar") return true;
  if (key === "challenges") {
    // Real Challenges UI is the canvas lobby (`challenge-lobby`); `#challengesScreen` stays hidden.
    if (document.body.classList.contains("challenge-lobby")) return true;
    const el = document.getElementById("challengesScreen");
    return !!(el && !el.classList.contains("hidden")) && !playing;
  }
  if (key === "home") {
    const start = document.getElementById("startScreen");
    return !!(start && !start.classList.contains("hidden")) && !playing;
  }
  const id = THEME_SCREEN_ELEMENT_IDS[key] || `${key}Screen`;
  const el = document.getElementById(id);
  if (!el) return false;
  return !el.classList.contains("hidden") && !playing;
}

export function normalizeThemeData(raw: unknown): ThemeData {
  const base = emptyThemeData();
  if (!raw || typeof raw !== "object") return base;
  const o = raw as Record<string, unknown>;
  const c = (o.colors && typeof o.colors === "object" ? o.colors : {}) as Record<string, unknown>;
  const hex = (v: unknown, fallback: string) =>
    typeof v === "string" && /^#[0-9a-fA-F]{6}$/.test(v) ? v.toLowerCase() : fallback;
  const colors: ThemeColors = {
    backgroundColor: hex(c.backgroundColor, base.colors.backgroundColor),
    backgroundGradient: c.backgroundGradient === true,
    backgroundGradientTo: hex(c.backgroundGradientTo, base.colors.backgroundGradientTo),
    buttonsColor: hex(c.buttonsColor, base.colors.buttonsColor),
    textColor: hex(c.textColor, base.colors.textColor),
    buttonsGradient: c.buttonsGradient !== false,
    buttonsGradientTo: hex(c.buttonsGradientTo, base.colors.buttonsGradientTo),
    textGradient: c.textGradient === true,
    textGradientTo: hex(c.textGradientTo, base.colors.textGradientTo),
    ballsColor: hex(c.ballsColor, base.colors.ballsColor),
    ballsGradient: c.ballsGradient === true,
    ballsGradientTo: hex(c.ballsGradientTo, base.colors.ballsGradientTo),
    dontChangeBackground: c.dontChangeBackground === true,
    dontChangeBalls: c.dontChangeBalls === true,
    dontChangeButtons: c.dontChangeButtons === true,
    dontChangeText: c.dontChangeText === true,
  };
  const decorations: ThemeDecoration[] = [];
  if (Array.isArray(o.decorations)) {
    for (const d of o.decorations) {
      if (!d || typeof d !== "object") continue;
      const row = d as Record<string, unknown>;
      const imageData = typeof row.imageData === "string" ? row.imageData : "";
      if (!imageData.startsWith("data:image/")) continue;
      const kind: ThemeDecoration["kind"] =
        row.kind === "particles" ? "particles" : row.kind === "animated" ? "animated" : "static";
      const frames =
        Array.isArray(row.frames)
          ? row.frames
              .filter((f): f is string => typeof f === "string" && f.startsWith("data:image/"))
              .slice(0, 10)
          : undefined;
      const screensRaw = Array.isArray(row.screens) ? row.screens : [];
      const screens: ThemeScreenTarget[] = [];
      for (const s of screensRaw) {
        const n = normalizeScreen(s);
        if (n) screens.push(n);
      }
      if (kind === "animated") {
        const onlyLegacy =
          screens.length > 0 && screens.every((s) => s === "all" || s === "home");
        if (onlyLegacy) {
          screens.length = 0;
          screens.push("background");
        }
      }
      if (!screens.length) {
        screens.push(
          row.anchor === "sidebar"
            ? "sidebar"
            : kind === "animated" || kind === "particles"
              ? "background"
              : "home",
        );
      }
      const num = (v: unknown, fallback: number) =>
        typeof v === "number" && Number.isFinite(v) ? v : fallback;
      const opacityRaw = num(row.opacity, 1);
      const opacity = Math.max(0, Math.min(1, opacityRaw));
      const isBg =
        screens.includes("background") ||
        row.layer === "back" ||
        kind === "particles";
      const particleConfig =
        kind === "particles" ? normalizeParticleConfig(row.particleConfig) : undefined;
      const attachSelector =
        typeof row.attachSelector === "string" &&
        row.attachSelector.trim().length > 0 &&
        row.attachSelector.length < 220
          ? row.attachSelector.trim()
          : undefined;
      const pctFallbackX = isBg || kind === "particles" ? 0 : 10;
      const pctFallbackY = isBg || kind === "particles" ? 0 : 10;
      decorations.push({
        id: typeof row.id === "string" && row.id ? row.id : `dec_${decorations.length}`,
        kind,
        imageData,
        frames:
          (kind === "animated" || kind === "particles") && frames?.length ? frames : undefined,
        anchor:
          row.anchor === "sidebar" || (screens.includes("sidebar") && !screens.includes("background"))
            ? "sidebar"
            : "viewport",
        xPct: attachSelector
          ? clampNum(row.xPct, pctFallbackX, -250, 250)
          : clampNum(row.xPct, pctFallbackX, -20, 100),
        yPct: attachSelector
          ? clampNum(row.yPct, pctFallbackY, -250, 250)
          : clampNum(row.yPct, pctFallbackY, -20, 100),
        widthPct: Math.max(
          1,
          Math.min(attachSelector ? 300 : 100, num(row.widthPct, isBg || kind === "particles" ? 100 : 12)),
        ),
        heightPct: Math.max(
          1,
          Math.min(attachSelector ? 300 : 100, num(row.heightPct, isBg || kind === "particles" ? 100 : 12)),
        ),
        screens,
        zIndex: typeof row.zIndex === "number" ? row.zIndex : undefined,
        opacity,
        layer: isBg || row.layer === "back" || kind === "particles" ? "back" : "front",
        particleConfig,
        attachSelector,
        rotationDeg: (() => {
          const r = num(row.rotationDeg, 0);
          if (!Number.isFinite(r) || r === 0) return undefined;
          const n = ((Math.round(r) % 360) + 360) % 360;
          return n === 0 ? undefined : n;
        })(),
      });
    }
  }
  const music = normalizeThemeMusic(o.music) ?? { ...DEFAULT_THEME_MUSIC, cells: [] };
  return { colors, decorations, music };
}

/** Resolve which saved theme is currently live for players. */
export function pickActiveThemeRow(
  rows: Array<{
    id: string;
    name: string;
    data: unknown;
    isActive: boolean;
    scheduleStart: Date | string | null;
    scheduleEnd: Date | string | null;
  }>,
  now = new Date(),
): { id: string; name: string; data: ThemeData } | null {
  const t = now.getTime();
  const scheduled = rows.find((r) => {
    if (!r.scheduleStart) return false;
    const start = new Date(r.scheduleStart).getTime();
    if (Number.isNaN(start) || start > t) return false;
    if (!r.scheduleEnd) return true;
    const end = new Date(r.scheduleEnd).getTime();
    return !Number.isNaN(end) && end > t;
  });
  if (scheduled) {
    return { id: scheduled.id, name: scheduled.name, data: normalizeThemeData(scheduled.data) };
  }
  const manual = rows.find((r) => r.isActive);
  if (manual) {
    return { id: manual.id, name: manual.name, data: normalizeThemeData(manual.data) };
  }
  return null;
}
